import React, { useState, useEffect } from 'react';
import { useShop } from '../context/ShopContext';
import { X, CheckCircle, ShieldCheck, CreditCard, QrCode, Building2, Truck, Sparkles, Check, ArrowRight, Tag, Copy } from 'lucide-react';
import { formatINR } from '../data/products';
import { resolveProductImage, handleProductImageError } from '../utils/imageHelper';
import confetti from 'canvas-confetti';

export default function CheckoutModal() {
  const {
    isCheckoutOpen,
    setIsCheckoutOpen,
    cart,
    cartSubtotal,
    discountAmount,
    gstAmount,
    cartTotal,
    shippingFee,
    freeShippingThreshold,
    courierCharge,
    clearCart,
    addNewOrder,
    addToast,
    masterMetalRates,
    calculateProductPrice,
    storeSettings,
    couponCode,
    appliedDiscountPercent,
    couponMessage,
    applyCoupon,
    removeCoupon,
    currentCustomer
  } = useShop();

  const [formData, setFormData] = useState({
    fullName: currentCustomer?.name || '',
    phone: currentCustomer?.phone || '',
    email: currentCustomer?.email || '',
    address: currentCustomer?.address || '',
    city: currentCustomer?.city || 'Jaipur',
    state: 'Rajasthan',
    pincode: '302002'
  });

  // Sync if customer logs in or details change
  useEffect(() => {
    if (currentCustomer && isCheckoutOpen) {
      setFormData(prev => ({
        ...prev,
        fullName: prev.fullName || currentCustomer.name || '',
        phone: prev.phone || currentCustomer.phone || '',
        email: prev.email || currentCustomer.email || '',
        address: prev.address || currentCustomer.address || '',
        city: prev.city || currentCustomer.city || 'Jaipur'
      }));
    }
  }, [currentCustomer, isCheckoutOpen]);

  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [isProcessingRzp, setIsProcessingRzp] = useState(false);
  const [paidPaymentId, setPaidPaymentId] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [inputCoupon, setInputCoupon] = useState('');
  const [upiUtr, setUpiUtr] = useState('');
  const [copiedUpi, setCopiedUpi] = useState(false);

  const handleApplyCoupon = (e) => {
    if (e) e.preventDefault();
    if (!inputCoupon.trim()) return;
    applyCoupon(inputCoupon);
    setInputCoupon('');
  };

  const copyUpiId = () => {
    const upi = storeSettings?.upiId || 'mljewellers@icici';
    navigator.clipboard?.writeText(upi);
    setCopiedUpi(true);
    addToast('UPI ID copied to clipboard!', 'success');
    setTimeout(() => setCopiedUpi(false), 2500);
  };

  if (!isCheckoutOpen) return null;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const finalizeOrder = (methodName, paymentId = '') => {
    // Trigger celebration confetti
    try {
      confetti({
        particleCount: 120,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#d4af37', '#faeb92', '#10b981', '#ffffff']
      });
    } catch (err) {
      console.log(err);
    }

    const generatedId = `MLJ-${Math.floor(100000 + Math.random() * 900000)}`;
    setOrderId(generatedId);
    if (paymentId) setPaidPaymentId(paymentId);

    // Save order into Admin Orders state
    if (addNewOrder) {
      addNewOrder({
        id: generatedId,
        date: 'Just Now',
        customer: {
          name: formData.fullName,
          phone: formData.phone,
          email: formData.email,
          address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`
        },
        items: cart.map(c => {
          const product = c?.product || c;
          const livePricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
          const unitPrice = livePricing?.finalPrice || Number(product?.price) || 0;
          return {
            name: product?.name || 'Jewellery Item',
            qty: Number(c?.quantity) || 1,
            price: unitPrice,
            metal: product?.metal || '22K Gold'
          };
        }),
        paymentMethod: paymentId 
          ? `Razorpay Online (${paymentId})` 
          : (paymentMethod === 'upi' ? `Showroom UPI QR ${upiUtr ? `(UTR: ${upiUtr})` : ''}` : methodName),
        total: cartTotal,
        status: paymentId ? 'Paid / Confirmed' : (paymentMethod === 'upi' ? 'UPI Verification Pending' : 'Processing'),
        notes: paymentId ? `Razorpay Payment ID: ${paymentId}` : (upiUtr ? `UPI Transaction Reference: ${upiUtr}` : '')
      });
    }

    setIsSubmitted(true);
    setIsProcessingRzp(false);
    clearCart();
    addToast(paymentId ? `Payment of ${formatINR(cartTotal)} successful! Order #${generatedId} confirmed.` : (paymentMethod === 'upi' ? 'Order placed via Showroom UPI QR! Recorded in Admin Orders.' : 'Order placed successfully! Recorded in Admin Orders.'), 'success');
  };

  const handlePlaceOrder = (e) => {
    e.preventDefault();

    if (!formData.fullName.trim() || !formData.phone.trim() || !formData.address.trim()) {
      addToast('Please enter your full name, phone number, and delivery address.', 'error');
      return;
    }

    // 1. RAZORPAY GATEWAY
    if (paymentMethod === 'razorpay') {
      setIsProcessingRzp(true);

      const rzpKey = storeSettings?.razorpayKeyId || 'rzp_test_TkcMWJI1AFGNfu';

      const launchRazorpay = () => {
        if (!window.Razorpay) {
          setIsProcessingRzp(false);
          addToast('Razorpay checkout SDK could not be loaded. Please try again.', 'error');
          return;
        }

        const options = {
          key: rzpKey,
          amount: Math.round(cartTotal * 100),
          currency: 'INR',
          name: storeSettings?.storeName || 'M.L. Jewellers & Kitchenware',
          description: `Order Checkout (${cart.length} item${cart.length > 1 ? 's' : ''})`,
          image: storeSettings?.logoUrl || '',
          prefill: {
            name: formData.fullName,
            contact: formData.phone,
            email: formData.email
          },
          notes: {
            delivery_address: `${formData.address}, ${formData.city}, ${formData.state} - ${formData.pincode}`
          },
          theme: {
            color: '#d4af37'
          },
          handler: function (response) {
            finalizeOrder('Razorpay Online (UPI/Cards)', response.razorpay_payment_id);
          },
          modal: {
            ondismiss: function () {
              setIsProcessingRzp(false);
              addToast('Payment cancelled or closed.', 'info');
            }
          }
        };

        try {
          const rzp = new window.Razorpay(options);
          rzp.on('payment.failed', function (resp) {
            setIsProcessingRzp(false);
            addToast(`Payment failed: ${resp.error?.description || 'Gateway error'}`, 'error');
          });
          rzp.open();
        } catch (err) {
          console.error('Razorpay modal open error:', err);
          setIsProcessingRzp(false);
          addToast('Could not open Razorpay checkout: ' + err.message, 'error');
        }
      };

      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.async = true;
        script.onload = launchRazorpay;
        script.onerror = () => {
          setIsProcessingRzp(false);
          addToast('Failed to load Razorpay checkout script.', 'error');
        };
        document.body.appendChild(script);
      } else {
        launchRazorpay();
      }
      return;
    }

    // 2. UPI QR CODE PAYMENT METHOD
    finalizeOrder('Showroom UPI QR Code', upiUtr ? `UTR-${upiUtr}` : '');
  };

  const handleClose = () => {
    setIsCheckoutOpen(false);
    setIsSubmitted(false);
    setIsProcessingRzp(false);
    setPaidPaymentId('');
  };

  return (
    <div 
      className={`modal-overlay ${isCheckoutOpen ? 'active' : ''}`}
      onClick={handleClose}
    >
      <div 
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '880px' }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            background: 'rgba(255, 255, 255, 0.08)',
            border: '1px solid var(--border-subtle)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10
          }}
        >
          <X size={18} />
        </button>

        {isSubmitted ? (
          /* Order Success Confirmation Screen */
          <div style={{
            padding: '3.5rem 2rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'rgba(16, 185, 129, 0.15)',
              border: '2px solid #10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1.5rem'
            }}>
              <CheckCircle size={44} color="#34d399" />
            </div>

            <span className="badge badge-gold" style={{ marginBottom: '0.8rem' }}>
              <Sparkles size={12} /> Royal Order Confirmed
            </span>

            <h2 style={{
              fontSize: '2rem',
              color: '#fff',
              fontWeight: 700,
              marginBottom: '0.5rem'
            }}>
              Thank You for Trusting <span className="text-gold-gradient">M.L. Jewellers</span>
            </h2>

            <p style={{
              color: 'var(--text-secondary)',
              fontSize: '1rem',
              maxWidth: '520px',
              lineHeight: 1.6,
              marginBottom: '2rem'
            }}>
              Your precious order <strong style={{ color: 'var(--gold-400)' }}>#{orderId}</strong> has been secured with tamper-evident vault packaging and dispatched for insured courier transit.
            </p>

            {/* Order details summary card */}
            <div style={{
              background: '#0d0d16',
              border: '1px solid var(--border-gold)',
              borderRadius: 'var(--radius-md)',
              padding: '1.5rem',
              width: '100%',
              maxWidth: '500px',
              textAlign: 'left',
              marginBottom: '2rem',
              fontSize: '0.88rem'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Estimated Delivery:</span>
                <strong style={{ color: '#34d399' }}>Within 3-4 Business Days</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Recipient:</span>
                <span style={{ color: '#fff' }}>{formData.fullName}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ color: 'var(--text-muted)' }}>Destination:</span>
                <span style={{ color: '#fff' }}>{formData.city}, {formData.state} - {formData.pincode}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.6rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <span style={{ color: 'var(--text-muted)' }}>Payment Mode:</span>
                <span style={{ color: paidPaymentId ? '#34d399' : 'var(--gold-400)', fontWeight: 600 }}>
                  {paidPaymentId ? `Razorpay: ${paidPaymentId}` : (paymentMethod === 'razorpay' ? 'Razorpay Online' : paymentMethod.toUpperCase())}
                </span>
              </div>
              {paidPaymentId && (
                <div style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 700, marginTop: '4px', textAlign: 'right' }}>
                  ✓ 100% Verified & Secured via Razorpay
                </div>
              )}
            </div>

            <button className="btn btn-gold" onClick={handleClose}>
              Continue Shopping
            </button>
          </div>
        ) : (
          /* Checkout Form & Order Summary */
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.3fr 1fr',
            gap: '2rem',
            padding: '2rem'
          }} className="checkout-grid">

            {/* Left Column: Details & Payment */}
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: 'var(--gold-400)',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                marginBottom: '0.4rem'
              }}>
                <ShieldCheck size={16} /> 100% Insured Delivery
              </div>
              <h2 style={{ fontSize: '1.6rem', color: '#fff', fontWeight: 700, marginBottom: '1.5rem' }}>
                Express <span className="text-gold-gradient">Checkout</span>
              </h2>

              <form onSubmit={handlePlaceOrder}>
                {/* Contact & Shipping Inputs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.9rem', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        required
                        value={formData.fullName}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="e.g. Aditi Singhal"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        Phone (for OTP Delivery) *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        className="input-luxury"
                        placeholder="10-digit Mobile"
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                      Email Address (for Digital Hallmark Certificate) *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="input-luxury"
                      placeholder="aditi@example.com"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                      Delivery Street Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      required
                      value={formData.address}
                      onChange={handleChange}
                      className="input-luxury"
                      placeholder="House / Flat / Landmark"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1.2fr 1fr', gap: '0.8rem' }}>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        required
                        value={formData.city}
                        onChange={handleChange}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        State *
                      </label>
                      <input
                        type="text"
                        name="state"
                        required
                        value={formData.state}
                        onChange={handleChange}
                        className="input-luxury"
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                        PIN Code *
                      </label>
                      <input
                        type="text"
                        name="pincode"
                        required
                        value={formData.pincode}
                        onChange={handleChange}
                        className="input-luxury"
                      />
                    </div>
                  </div>
                </div>

                {/* Payment Method Selector */}
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 700, color: '#fff', marginBottom: '0.7rem' }}>
                    Select Payment Method
                  </label>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    {/* Razorpay Gateway */}
                    <div
                      onClick={() => setPaymentMethod('razorpay')}
                      style={{
                        padding: '0.85rem',
                        borderRadius: 'var(--radius-sm)',
                        border: paymentMethod === 'razorpay' ? '2px solid var(--gold-400)' : '1px solid var(--border-subtle)',
                        background: paymentMethod === 'razorpay' ? 'rgba(212, 175, 55, 0.14)' : '#0f0f18',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.65rem',
                        position: 'relative'
                      }}
                    >
                      <Sparkles size={20} color="var(--gold-400)" />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff' }}>Razorpay Online</span>
                          <span style={{
                            background: 'linear-gradient(135deg, #d4af37, #f59e0b)',
                            color: '#000',
                            fontSize: '0.58rem',
                            fontWeight: 900,
                            padding: '0.1rem 0.45rem',
                            borderRadius: '999px'
                          }}>
                            INSTANT
                          </span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Cards, UPI, NetBanking & Wallets</div>
                      </div>
                    </div>

                    {/* Direct UPI QR Code */}
                    <div
                      onClick={() => setPaymentMethod('upi')}
                      style={{
                        padding: '0.85rem',
                        borderRadius: 'var(--radius-sm)',
                        border: paymentMethod === 'upi' ? '2px solid var(--gold-400)' : '1px solid var(--border-subtle)',
                        background: paymentMethod === 'upi' ? 'rgba(212, 175, 55, 0.14)' : '#0f0f18',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.65rem',
                        position: 'relative'
                      }}
                    >
                      <QrCode size={20} color="var(--gold-400)" />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#fff' }}>Showroom UPI QR</span>
                          <span style={{
                            background: '#10b981',
                            color: '#fff',
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            padding: '0.1rem 0.45rem',
                            borderRadius: '999px'
                          }}>
                            SCAN & PAY
                          </span>
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>Scan via GPay, PhonePe, Paytm</div>
                      </div>
                    </div>
                  </div>

                  {/* UPI QR Display Card when UPI is selected */}
                  {paymentMethod === 'upi' && (
                    <div style={{
                      marginTop: '0.9rem',
                      padding: '1.1rem',
                      background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(13, 13, 22, 0.95) 100%)',
                      border: '1px solid var(--border-gold)',
                      borderRadius: 'var(--radius-sm)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.85rem',
                      textAlign: 'center'
                    }}>
                      <div style={{
                        fontSize: '0.84rem',
                        fontWeight: 700,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <QrCode size={18} color="var(--gold-400)" />
                        <span>Scan QR Code to Pay via any UPI App</span>
                      </div>

                      {/* Scannable Dynamic QR Code Image */}
                      <div style={{
                        background: '#ffffff',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '2px solid var(--gold-400)',
                        boxShadow: '0 4px 14px rgba(212, 175, 55, 0.25)',
                        display: 'inline-block'
                      }}>
                        <img
                          src={storeSettings?.upiQrImage || `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(`upi://pay?pa=${storeSettings?.upiId || 'mljewellers@icici'}&pn=${encodeURIComponent(storeSettings?.storeName || 'ML Jewellers')}&am=${cartTotal}&cu=INR&tn=Order`)}`}
                          alt="Showroom UPI QR Code"
                          style={{ width: '160px', height: '160px', display: 'block' }}
                        />
                      </div>

                      <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <div>Amount to Pay: <strong style={{ color: 'var(--gold-400)', fontSize: '0.95rem' }}>{formatINR(cartTotal)}</strong></div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '4px' }}>
                          <span>Official UPI ID: <strong style={{ color: '#fff' }}>{storeSettings?.upiId || 'mljewellers@icici'}</strong></span>
                          <button
                            type="button"
                            onClick={copyUpiId}
                            style={{
                              background: 'rgba(212, 175, 55, 0.15)',
                              border: '1px solid var(--border-gold)',
                              color: 'var(--gold-400)',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '0.72rem',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            {copiedUpi ? <Check size={12} /> : <Copy size={12} />}
                            <span>{copiedUpi ? 'Copied' : 'Copy'}</span>
                          </button>
                        </div>
                      </div>

                      <div style={{ width: '100%', textAlign: 'left', marginTop: '4px' }}>
                        <label style={{ display: 'block', fontSize: '0.74rem', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>
                          UPI Transaction Ref / UTR Number (Optional / For Instant Dispatch)
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. 4289XXXXXXXX (12-digit UTR)"
                          value={upiUtr}
                          onChange={(e) => setUpiUtr(e.target.value)}
                          className="input-luxury"
                          style={{ fontSize: '0.82rem', padding: '0.55rem 0.8rem' }}
                        />
                      </div>

                      <div style={{ fontSize: '0.72rem', color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <CheckCircle size={13} />
                        <span>Supported: Google Pay, PhonePe, Paytm, BHIM UPI & Banking Apps</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={isProcessingRzp}
                  className="btn btn-gold"
                  style={{
                    width: '100%',
                    padding: '0.95rem',
                    fontSize: '0.95rem',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isProcessingRzp ? 0.7 : 1,
                    cursor: isProcessingRzp ? 'wait' : 'pointer'
                  }}
                >
                  {isProcessingRzp ? (
                    <>
                      <Sparkles size={16} /> Opening Razorpay Checkout...
                    </>
                  ) : paymentMethod === 'razorpay' ? (
                    <>
                      <ShieldCheck size={16} /> Pay {formatINR(cartTotal)} via Razorpay & Confirm
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={16} /> Confirm Order via Showroom QR ({formatINR(cartTotal)})
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Right Column: Order Summary */}
            <div style={{
              background: '#0d0d16',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column'
            }}>
              <h3 style={{ fontSize: '1.1rem', color: '#fff', fontWeight: 700, marginBottom: '1rem' }}>
                Order Summary
              </h3>

              {/* Items preview */}
              <div style={{
                maxHeight: '220px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem',
                marginBottom: '1rem',
                paddingRight: '0.3rem'
              }}>
                {cart.map((item, i) => {
                  const product = item?.product || item;
                  if (!product) return null;
                  const livePricing = calculateProductPrice ? calculateProductPrice(product, masterMetalRates) : null;
                  const unitPrice = livePricing?.finalPrice || Number(product.price) || 0;
                  const rawImg = (Array.isArray(product.images) && product.images[0]) || product.image;
                  const prodImg = resolveProductImage(rawImg, product);
                  const prodName = product.name || 'Fine Jewellery';
                  const qty = Number(item.quantity) || 1;

                  return (
                    <div
                      key={product.id || i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem',
                        padding: '0.5rem',
                        background: 'rgba(255, 255, 255, 0.02)',
                        borderRadius: 'var(--radius-sm)'
                      }}
                    >
                      <img
                        src={prodImg}
                        alt={prodName}
                        onError={(e) => handleProductImageError(e, product)}
                        style={{ width: '48px', height: '48px', borderRadius: '4px', objectFit: 'cover', background: '#12121c' }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.82rem', color: '#fff', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {prodName}
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          Qty: {qty} × {formatINR(unitPrice)}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Interactive Coupon Code Form */}
              <div style={{
                marginBottom: '1rem',
                padding: '0.85rem',
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(212, 175, 55, 0.25)',
                borderRadius: 'var(--radius-sm)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '0.5rem', fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
                  <Tag size={14} color="var(--gold-400)" />
                  <span>Have a Privilege Coupon?</span>
                </div>

                <div style={{ display: 'flex', gap: '6px' }}>
                  <input
                    type="text"
                    placeholder="Enter Coupon (e.g. ROYAL10)"
                    value={inputCoupon}
                    onChange={(e) => setInputCoupon(e.target.value.toUpperCase())}
                    style={{
                      flex: 1,
                      background: '#151522',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '4px',
                      padding: '0.5rem 0.75rem',
                      color: '#fff',
                      fontSize: '0.82rem',
                      textTransform: 'uppercase',
                      outline: 'none'
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleApplyCoupon}
                    className="btn btn-gold btn-sm"
                    style={{ padding: '0.5rem 0.9rem', fontSize: '0.78rem' }}
                  >
                    Apply
                  </button>
                </div>

                {/* Coupon Applied Tag */}
                {couponCode && (
                  <div style={{
                    marginTop: '0.6rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.4rem 0.7rem',
                    background: 'rgba(16, 185, 129, 0.12)',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    borderRadius: '4px',
                    fontSize: '0.74rem',
                    color: '#34d399'
                  }}>
                    <span>✓ <strong>{couponCode}</strong> Applied (-{appliedDiscountPercent}%)</span>
                    <button
                      type="button"
                      onClick={removeCoupon}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#fb7185',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '0.72rem'
                      }}
                    >
                      Remove
                    </button>
                  </div>
                )}

                {couponMessage && !couponCode && (
                  <div style={{
                    marginTop: '0.45rem',
                    fontSize: '0.74rem',
                    color: '#fb7185'
                  }}>
                    {couponMessage?.text || couponMessage}
                  </div>
                )}
              </div>

              {/* Calculations */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.45rem',
                fontSize: '0.82rem',
                color: 'var(--text-secondary)',
                marginTop: 'auto',
                borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                paddingTop: '0.9rem'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Subtotal</span>
                  <span style={{ color: '#fff' }}>{formatINR(cartSubtotal)}</span>
                </div>
                {discountAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#34d399' }}>
                    <span>Privilege Discount</span>
                    <span>- {formatINR(discountAmount)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>GST (3%)</span>
                  <span style={{ color: '#fff' }}>{formatINR(gstAmount)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Insured Courier</span>
                  <span style={{ color: (shippingFee > 0 ? 'var(--gold-400)' : '#34d399'), fontWeight: 600 }}>
                    {shippingFee > 0 ? formatINR(shippingFee) : 'FREE'}
                  </span>
                </div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  paddingTop: '0.6rem',
                  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                  fontSize: '1.15rem',
                  fontWeight: 700,
                  color: '#fff'
                }}>
                  <span>Final Total</span>
                  <span style={{ color: 'var(--gold-400)', fontFamily: 'var(--font-serif)' }}>
                    {formatINR(cartTotal)}
                  </span>
                </div>
              </div>

              <div style={{
                marginTop: '1.2rem',
                padding: '0.8rem',
                background: 'rgba(212, 175, 55, 0.06)',
                border: '1px solid var(--border-gold)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.74rem',
                color: 'var(--text-secondary)'
              }}>
                🔒 Guaranteed safe checkout. Includes official BIS Hallmark Assay Certificate & Tax Invoice.
              </div>
            </div>

          </div>
        )}

      </div>

      <style>{`
        @media (max-width: 768px) {
          .checkout-grid {
            grid-template-columns: 1fr !important;
            padding: 1.2rem !important;
          }
        }
      `}</style>
    </div>
  );
}
